import { Component, OnDestroy, OnInit } from '@angular/core';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { Header } from '@aptsi-types';
import { jsPDF } from 'jspdf';
import 'jspdf-autotable';
import 'chart.js/auto';
import { generateAccessiblePatterns } from 'src/app/shared/chart-patterns/chart-patterns';
import {
  DataAssetInventoryItem,
  DataAssetsInventoryReportResponse,
  DataProtectionDashboardChartData,
  DataProtectionReportService,
  ProtectionProgramReportItem,
  ProtectionProgramStatusReportResponse,
} from './reports.service';

/**
 * Canonical order keeps each program status associated with the same accessible
 * pattern even when the API returns the statuses in a different order.
 */
const PROGRAM_STATUS_PATTERN_ORDER: readonly string[] = [
  'PROGRAM_NOT_CREATED',
  'AWAITING_IMPLEMENTATION',
  'IMPLEMENTATION_IN_PROGRESS',
  'AWAITING_VERIFICATION',
  'PROGRAM_COMPLETED',
  'STATUS_REVIEW_REQUIRED',
];

@Component({
  selector: 'app-reports',
  templateUrl: './reports.component.html',
  styleUrls: ['./reports.component.scss'],
})
export class ReportsComponent implements OnInit, OnDestroy {
  dataAssetsInventoryReport: DataAssetInventoryItem[] = [];
  protectionProgramStatusReport: ProtectionProgramReportItem[] = [];

  inventoryReportSummary: DataAssetsInventoryReportResponse = {};
  programReportSummary: ProtectionProgramStatusReportResponse = {};
  dashboardChartResponse: DataProtectionDashboardChartData = {};

  programStatusChartData: any = { labels: [], datasets: [] };
  eligibleAssetsChartData: any = { labels: [], datasets: [] };

  sanitizedPdfUrl: SafeResourceUrl | null = null;
  isView = false;
  isLoadingReports = false;
  isLoadingChart = false;
  reportError = '';
  chartError = '';

  private pdfBlobUrl: string | null = null;

  readonly filterOptions = [
    { label: 'Last 7 days', value: 'last_7_days' },
    { label: 'Last month', value: 'last_month' },
    { label: 'All time', value: 'all_time' },
  ];

  selectedFilter = 'all_time';

  readonly headers: { inventory: Header[]; programs: Header[] } = {
    inventory: [
      { name: 'Data Asset ID', property: 'dataAssetId' },
      { name: 'Data Asset Name', property: 'dataAssetName' },
      { name: 'Description', property: 'description' },
      { name: 'Classification', property: 'classification' },
      { name: 'Mapping Status', property: 'mappingStatus' },
      { name: 'System Assets', property: 'systemAssetsDisplay' },
      { name: 'Security Zones', property: 'securityZonesDisplay' },
      { name: 'Source System', property: 'sourceSystem' },
      { name: 'Imported At', property: 'importedAt' },
      { name: 'Updated At', property: 'updatedAt' },
    ],
    programs: [
      { name: 'Program Name', property: 'programName' },
      { name: 'Program Owner', property: 'programOwner' },
      { name: 'Data Asset', property: 'dataAssetName' },
      { name: 'Start Date', property: 'startDate' },
      { name: 'Target Completion', property: 'targetCompletionDate' },
      { name: 'Program Status', property: 'programStatus' },
      { name: 'Implementation Status', property: 'implementationStatus' },
      { name: 'Verification Status', property: 'verificationStatus' },
      { name: 'Verification Date', property: 'verificationDate' },
      { name: 'Overdue', property: 'overdueDisplay' },
      { name: 'Upcoming', property: 'upcomingDisplay' },
    ],
  };

  readonly programStatusChartOptions: any = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'bottom',
        labels: {
          color: '#4b5870',
          usePointStyle: true,
          pointStyle: 'circle',
          boxWidth: 8,
          padding: 14,
        },
      },
      tooltip: {
        enabled: true,
        backgroundColor: '#07112e',
        titleColor: '#ffffff',
        bodyColor: '#ffffff',
        padding: 10,
        cornerRadius: 8,
        callbacks: {
          label: (context: any) => `${context.label}: ${context.parsed ?? 0} programs`,
        },
      },
    },
  };

  readonly eligibleAssetsChartOptions: any = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { display: false },
      tooltip: {
        enabled: true,
        backgroundColor: '#07112e',
        titleColor: '#ffffff',
        bodyColor: '#ffffff',
        padding: 10,
        cornerRadius: 8,
        callbacks: {
          label: (context: any) =>
            `Eligible assets: ${Number(context.parsed?.y ?? 0).toLocaleString()}`,
        },
      },
    },
    scales: {
      x: {
        grid: { display: false },
        border: { display: false },
        ticks: { color: '#6f7b91', maxRotation: 0, autoSkip: true },
        title: { display: true, text: 'Data Classification', color: '#4b5870', font: { weight: '600' } },
      },
      y: {
        min: 0,
        grid: { color: '#e9edf3' },
        border: { display: false },
        ticks: { color: '#6f7b91', callback: (value: string | number) => Number(value).toLocaleString() },
        title: { display: true, text: 'Total Eligible Assets', color: '#4b5870', font: { weight: '600' } },
      },
    },
  };

  constructor(
    private readonly dataProtectionReportService: DataProtectionReportService,
    private readonly sanitizer: DomSanitizer,
  ) {}

  ngOnInit(): void {
    void this.fetchReportData();
    void this.fetchChartData();

    
  }

  ngOnDestroy(): void {
    this.revokePdfBlobUrl();
  }

  onFilterChange(filter: string): void {
    if (!filter) {
      return;
    }

    this.selectedFilter = filter;
    void this.fetchReportData(filter);
    void this.fetchChartData(filter);
  }

  async fetchReportData(filter?: string): Promise<void> {
    if (filter) {
      this.selectedFilter = filter;
    }

    this.isLoadingReports = true;
    this.reportError = '';

    try {
      const [inventoryResponse, programResponse] = await Promise.all([
  // Start the inventory API immediately.
  this.dataProtectionReportService
    .DataAssetsInventoryReport(this.selectedFilter)
    .toPromise(),

  // Start the program-status API after 400 ms.
  this.delay(400).then(() =>
    this.dataProtectionReportService
      .ProtectionProgramStatusReport(this.selectedFilter)
      .toPromise()
  ),
]);

      this.inventoryReportSummary = inventoryResponse ?? {};
      this.programReportSummary = programResponse ?? {};

      this.dataAssetsInventoryReport = (this.inventoryReportSummary.assets ?? []).map((item) => ({
        ...item,
        dataAssetId: item.dataAssetId ?? '—',
        dataAssetName: item.dataAssetName ?? '—',
        description: item.description ?? '—',
        classification: item.classification ?? '—',
        mappingStatus: item.mappingStatus ?? '—',
        systemAssetsDisplay: this.joinValues(item.systemAssets),
        securityZonesDisplay: this.joinValues(item.securityZones),
        sourceSystem: item.sourceSystem ?? '—',
        importedAt: item.importedAt ?? '—',
        updatedAt: item.updatedAt ?? '—',
      }));

      this.protectionProgramStatusReport = (this.programReportSummary.programs ?? []).map((item) => ({
        ...item,
        programName: item.programName ?? '—',
        programOwner: item.programOwner ?? '—',
        dataAssetName: item.dataAssetName ?? '—',
        startDate: item.startDate ?? '—',
        targetCompletionDate: item.targetCompletionDate ?? '—',
        programStatus: item.programStatus ?? '—',
        implementationStatus: item.implementationStatus ?? '—',
        verificationStatus: item.verificationStatus ?? '—',
        verificationDate: item.verificationDate ?? '—',
        overdueDisplay: item.overdue === true ? 'Yes' : item.overdue === false ? 'No' : '—',
        upcomingDisplay: item.upcoming === true ? 'Yes' : item.upcoming === false ? 'No' : '—',
      }));
    } catch (error) {
      console.error('Error fetching Data Protection reports:', error);
      this.reportError = 'Unable to load the reports. Check your connection and try again.';
      this.inventoryReportSummary = {};
      this.programReportSummary = {};
      this.dataAssetsInventoryReport = [];
      this.protectionProgramStatusReport = [];
    } finally {
      this.isLoadingReports = false;
    }
  }

  private delay(ms: number): Promise<void> {
  return new Promise(resolve => setTimeout(resolve, ms));
}

  async fetchChartData(filter?: string): Promise<void> {
    if (filter) {
      this.selectedFilter = filter;
    }

    this.isLoadingChart = true;
    this.chartError = '';

    try {
      const response = await this.dataProtectionReportService
        .getDpDashboardChartData(this.selectedFilter)
        .toPromise();

      this.dashboardChartResponse = response ?? {};
      const statusData = this.dashboardChartResponse.programStatusDistribution ?? [];
      const eligibleAssetsData = this.dashboardChartResponse.verifiedCoverageByClassification ?? [];

      // Normalize status keys, then attach patterns by a canonical status order.
      // This keeps each status's pattern stable if the API changes array ordering.
      const statusKeys = statusData.map((item) => {
        const status = String(item.status ?? '').trim().toUpperCase();
        return status || 'UNKNOWN';
      });
      const knownStatuses = new Set(PROGRAM_STATUS_PATTERN_ORDER);
      const additionalStatusKeys = Array.from(
        new Set(statusKeys.filter((status) => !knownStatuses.has(status))),
      ).sort();
      const statusPatternOrder = [
        ...PROGRAM_STATUS_PATTERN_ORDER,
        ...additionalStatusKeys,
      ];
      const statusPatterns = generateAccessiblePatterns(statusPatternOrder.length);
      const patternByStatus = new Map<string, CanvasPattern>();
      statusPatternOrder.forEach((status, index) => {
        patternByStatus.set(status, statusPatterns[index]);
      });

      this.programStatusChartData = {
        labels: statusData.map((item) => this.formatStatus(item.status)),
        datasets: [
          {
            label: 'Programs',
            data: statusData.map((item) => Number(item.count ?? 0)),
            backgroundColor: statusKeys.map(
              (status) => patternByStatus.get(status) ?? statusPatterns[0],
            ),
            borderColor: '#ffffff',
            borderWidth: 2,
            hoverOffset: 7,
          },
        ],
      };

      // Give each classification a stable accessible pattern. Mapping patterns
      // by classification keeps them consistent if the API changes row ordering.
      const classificationLabels = eligibleAssetsData.map((item) => {
        const classification = String(item.classification ?? '').trim();
        return classification || 'Unclassified';
      });
      const classificationPatternOrder = Array.from(new Set(classificationLabels)).sort((a, b) =>
        a.localeCompare(b),
      );
      const classificationPatterns = generateAccessiblePatterns(classificationPatternOrder.length);
      const patternByClassification = new Map<string, CanvasPattern>();
      classificationPatternOrder.forEach((classification, index) => {
        patternByClassification.set(classification, classificationPatterns[index]);
      });

      const eligibleAssetPatterns = classificationLabels.map(
        (classification) => patternByClassification.get(classification) ?? classificationPatterns[0],
      );
      this.eligibleAssetsChartData = {
        labels: classificationLabels,
        datasets: [
          {
            label: 'Total Eligible Assets',
            data: eligibleAssetsData.map((item) => Number(item.totalEligibleAssets ?? 0)),
            backgroundColor: eligibleAssetPatterns,
            hoverBackgroundColor: eligibleAssetPatterns,
            borderColor: '#2850ad',
            borderWidth: 1,
            borderRadius: 6,
            maxBarThickness: 54,
          },
        ],
      };
    } catch (error) {
      console.error('Error fetching Data Protection report charts:', error);
      this.chartError = 'Unable to load Data Protection chart data.';
      this.dashboardChartResponse = {};
      this.programStatusChartData = { labels: [], datasets: [] };
      this.eligibleAssetsChartData = { labels: [], datasets: [] };
    } finally {
      this.isLoadingChart = false;
    }
  }

  async generateAndDownloadPDF(): Promise<void> {
    await this.fetchReportData();
    await this.fetchChartData();

    if (!this.hasReportData) {
      return;
    }

    this.createPDFDocument().save(`DataProtectionReports_${this.selectedFilter}.pdf`);
  }

  async generateAndViewPDF(): Promise<void> {
    await this.fetchReportData();
    await this.fetchChartData();

    if (!this.hasReportData) {
      return;
    }

    this.revokePdfBlobUrl();
    const document = this.createPDFDocument();
    this.pdfBlobUrl = URL.createObjectURL(document.output('blob'));
    this.sanitizedPdfUrl = this.sanitizer.bypassSecurityTrustResourceUrl(this.pdfBlobUrl);
    this.isView = true;
  }

  get selectedFilterLabel(): string {
    return this.filterOptions.find((option) => option.value === this.selectedFilter)?.label ?? 'All time';
  }

  get hasReportData(): boolean {
    return this.dataAssetsInventoryReport.length > 0 ||
      this.protectionProgramStatusReport.length > 0 ||
      (this.dashboardChartResponse.programStatusDistribution?.length ?? 0) > 0 ||
      (this.dashboardChartResponse.verifiedCoverageByClassification?.length ?? 0) > 0;
  }

  close(): void {
    this.isView = false;
    this.sanitizedPdfUrl = null;
    this.revokePdfBlobUrl();
  }

  private joinValues(values: string[] | null | undefined): string {
    return Array.isArray(values) && values.length ? values.join(', ') : '—';
  }

  private formatStatus(value: string | null | undefined): string {
    return String(value ?? 'Unknown')
      .toLowerCase()
      .split('_')
      .map((part) => part ? part.charAt(0).toUpperCase() + part.slice(1) : part)
      .join(' ');
  }

  private revokePdfBlobUrl(): void {
    if (this.pdfBlobUrl) {
      URL.revokeObjectURL(this.pdfBlobUrl);
      this.pdfBlobUrl = null;
    }
  }

  private createPDFDocument(): jsPDF {
    const doc = new jsPDF({ orientation: 'landscape', format: 'a4' });
    const brandBlue: [number, number, number] = [14, 46, 128];
    const tableOptions = {
      theme: 'striped',
      headStyles: { fillColor: brandBlue, textColor: 255, fontStyle: 'bold' },
      styles: { fontSize: 7.5, cellPadding: 2, overflow: 'linebreak' },
      margin: { left: 10, right: 10 },
    };

    doc.setTextColor(7, 17, 46);
    doc.setFontSize(18);
    doc.text('Data Protection Reports', 10, 14);
    doc.setFontSize(9);
    doc.setTextColor(111, 123, 145);
    doc.text(`Reporting period: ${this.selectedFilterLabel}`, 10, 21);

    doc.setTextColor(23, 32, 51);
    doc.setFontSize(12);
    doc.text('Data Assets Inventory', 10, 30);
    (doc as any).autoTable({
      ...tableOptions,
      startY: 34,
      head: [[
        'Data Asset ID', 'Data Asset Name', 'Description', 'Classification',
        'Mapping Status', 'System Assets', 'Security Zones', 'Source System', 'Imported At', 'Updated At',
      ]],
      body: this.dataAssetsInventoryReport.map((item) => [
        item.dataAssetId ?? '', item.dataAssetName ?? '', item.description ?? '', item.classification ?? '',
        item.mappingStatus ?? '', item.systemAssetsDisplay ?? '', item.securityZonesDisplay ?? '',
        item.sourceSystem ?? '', item.importedAt ?? '', item.updatedAt ?? '',
      ]),
    });

    doc.addPage('a4', 'landscape');
    doc.setTextColor(23, 32, 51);
    doc.setFontSize(12);
    doc.text('Protection Program Status', 10, 16);
    (doc as any).autoTable({
      ...tableOptions,
      startY: 21,
      head: [[
        'Program Name', 'Owner', 'Data Asset', 'Start Date', 'Target Completion', 'Program Status',
        'Implementation Status', 'Verification Status', 'Verification Date', 'Overdue', 'Upcoming',
      ]],
      body: this.protectionProgramStatusReport.map((item) => [
        item.programName ?? '', item.programOwner ?? '', item.dataAssetName ?? '', item.startDate ?? '',
        item.targetCompletionDate ?? '', item.programStatus ?? '', item.implementationStatus ?? '',
        item.verificationStatus ?? '', item.verificationDate ?? '', item.overdueDisplay ?? '', item.upcomingDisplay ?? '',
      ]),
    });

    doc.addPage('a4', 'landscape');
    doc.setTextColor(23, 32, 51);
    doc.setFontSize(12);
    doc.text('Program Status Distribution', 10, 16);
    (doc as any).autoTable({
      ...tableOptions,
      startY: 21,
      head: [['Program Status', 'Count']],
      body: (this.dashboardChartResponse.programStatusDistribution ?? []).map((item) => [
        this.formatStatus(item.status), item.count ?? 0,
      ]),
    });

    const finalY = Number((doc as any).lastAutoTable?.finalY ?? 30);
    doc.setTextColor(23, 32, 51);
    doc.setFontSize(12);
    doc.text('Eligible Assets by Classification', 10, Math.min(finalY + 14, 175));
    (doc as any).autoTable({
      ...tableOptions,
      startY: Math.min(finalY + 18, 179),
      head: [['Classification', 'Total Eligible Assets']],
      body: (this.dashboardChartResponse.verifiedCoverageByClassification ?? []).map((item) => [
        item.classification ?? '', item.totalEligibleAssets ?? 0,
      ]),
    });

    return doc;
  }
}
